import type { PricingList as PricingI, PricingList as PricingListType } from 'api/types/pricing.types';
import AdditionalPercentOff from 'components/molecules/AdditionalPercentOff';
import { useConfig } from 'context/app-config';
import React from 'react';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectCustomer } from 'redux-stores/slices/authSlice';
import styled from 'styled-components';
import { toFixed } from 'utils/helpers';

import AddItems from './addItems';
import PricingList from './pricingList';

interface Props {
  type: PricingI.ServiceTypes;
  serviceDetails: PricingListType.CombinedService;
  currency?: string;
}

const StyledCol = styled(Col)`
  margin-bottom: 20px;
`;

const StyledH5 = styled.h5<{ hasDiscountedPrice?: boolean }>`
  margin-right: 2px;
  font-weight: ${({ hasDiscountedPrice = false }) => (hasDiscountedPrice ? 300 : 400)};
  text-decoration: ${({ hasDiscountedPrice = false }) => (hasDiscountedPrice ? 'line-through' : undefined)};
`;

const StyledHeading = styled.h2`
  color: ${({ theme }) => theme.layout?.page?.heading?.textColor};
`;

const PricingDetails: React.FC<Props> = ({ type, serviceDetails, currency }) => {
  const { t } = useTranslation();
  const config = useConfig();
  const customer = useSelector(selectCustomer);

  // TODO: returning placeholder loader
  if (!serviceDetails) return null;

  return (
    <Row className="bg-white py-8">
      <Col xs={12} className="mb-4">
        <StyledHeading className="mb-0">{t('common.pricing')}</StyledHeading>
      </Col>
      {(type === 'CLEAN_PRESS' || type === 'PRESS') && (
        <StyledCol>
          <h5 className="mb-0 font-weight-light">{t(`pricingPage.pricingDescriptions.${type}`)}</h5>
          <h5 className="mb-0 pt-2 font-weight-light">{t('pricingPage.priceVatExclusive')}</h5>
          {customer?.isPlus && <AdditionalPercentOff percentage={10} total={10} icon={config?.plusIcon} />}
        </StyledCol>
      )}
      <AddItems data={serviceDetails} currency={currency} serviceType={type} />
      {(type === 'WASH_FOLD' || type === 'HOME_CARE') && (
        <StyledCol>
          <h5 className="mb-0 font-weight-light">{t(`pricingPage.pricingDescriptions.${type}`)}</h5>
          {type === 'WASH_FOLD' && !serviceDetails.discountedPrice && (
            <Row noGutters>
              <StyledH5>{`AED ${toFixed(serviceDetails?.price, 2)}`}</StyledH5>
            </Row>
          )}
          {type === 'WASH_FOLD' && serviceDetails?.discountedPrice && (
            <Row noGutters>
              <StyledH5 hasDiscountedPrice={serviceDetails.discountedPrice !== undefined}>{`AED ${toFixed(serviceDetails?.price, 2)}`}</StyledH5>
              <StyledH5>{`AED ${toFixed(serviceDetails.discountedPrice, 2)}`}</StyledH5>
            </Row>
          )}
          <h5 className="mb-0 pt-2 font-weight-light">{t('pricingPage.priceVatExclusive')}</h5>
          {customer?.isPlus && <AdditionalPercentOff percentage={10} total={10} icon={config?.plusIcon} />}
        </StyledCol>
      )}
      <Col xs={12} className="p-0">
        <PricingList type={type} />
      </Col>
    </Row>
  );
};

PricingDetails.displayName = 'PricingDetails';

export default PricingDetails;
